import { ApiClient, ApiResponse } from '../client';
import { User, ListOptions } from '../models';

/**
 * Interfaz para direcciones de usuario
 */
export interface UserAddress {
  id: string;
  type: 'shipping' | 'billing';
  default: boolean;
  name: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  phone?: string;
}

/**
 * Servicio para gestionar usuarios (principalmente para administradores)
 */
export class UserService {
  private client: ApiClient;

  constructor(client: ApiClient) {
    this.client = client;
  }

  /**
   * Obtener listado de usuarios con filtros y paginación (admin)
   * @param options Opciones de filtrado y paginación
   */
  public async getUsers(options: ListOptions = {}): Promise<ApiResponse<User[]>> {
    const { page, limit, sort, order, search, filter } = options;

    // Construir parámetros de query
    const params: Record<string, any> = {
      page,
      limit,
      sort,
      order
    };

    // Añadir parámetro de búsqueda si existe
    if (search) {
      params.search = search;
    }

    // Añadir filtros específicos
    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        params[key] = value;
      });
    }

    return await this.client.get<User[]>('/users', { params });
  }

  /**
   * Obtener un usuario específico por ID (admin)
   * @param id ID del usuario
   */
  public async getUser(id: string): Promise<ApiResponse<User>> {
    return await this.client.get<User>(`/users/${id}`);
  }

  /**
   * Crear un nuevo usuario (admin)
   * @param userData Datos del usuario a crear
   */
  public async createUser(userData: Partial<User>): Promise<ApiResponse<User>> {
    return await this.client.post<User>('/users', userData);
  }

  /**
   * Actualizar un usuario existente (admin)
   * @param id ID del usuario
   * @param userData Datos actualizados
   */
  public async updateUser(id: string, userData: Partial<User>): Promise<ApiResponse<User>> {
    const response = await this.client.put<User>(`/users/${id}`, userData);
    if (response.success) {
      this.client.invalidateCacheForEndpoint(`/users/${id}`);
    }
    return response;
  }

  /**
   * Eliminar un usuario (admin)
   * @param id ID del usuario
   */
  public async deleteUser(id: string): Promise<ApiResponse<null>> {
    return await this.client.delete<null>(`/users/${id}`);
  }

  /**
   * Cambiar el estado de activación de un usuario (admin)
   * @param id ID del usuario
   * @param active Estado de activación
   */
  public async setUserActive(id: string, active: boolean): Promise<ApiResponse<User>> {
    return await this.client.put<User>(`/users/${id}`, { active });
  }

  /**
   * Cambiar el rol de un usuario (admin)
   * @param id ID del usuario
   * @param role Nuevo rol
   */
  public async changeUserRole(
    id: string,
    role: 'beeadmin' | 'admin' | 'distributor' | 'shop' | 'commercial' | 'customer'
  ): Promise<ApiResponse<User>> {
    return await this.client.put<User>(`/users/${id}`, { role });
  }

  /**
   * Obtener clientes de un comercial (para comerciales)
   */
  public async getCommercialCustomers(): Promise<ApiResponse<User[]>> {
    return await this.client.get<User[]>('/users/commercial/customers');
  }

  /**
   * Obtener tiendas asociadas a un distribuidor (para distribuidores)
   */
  public async getDistributorShops(): Promise<ApiResponse<User[]>> {
    return await this.client.get<User[]>('/users/distributor/shops');
  }

  /**
   * Añadir un cliente privado (para comerciales)
   * @param customerData Datos del cliente
   */
  public async addPrivateCustomer(customerData: {
    name: string;
    email: string;
    phone?: string;
    address?: {
      street: string;
      city: string;
      postalCode: string;
      country: string;
    };
    nif?: string;
    notes?: string;
  }): Promise<ApiResponse<any>> {
    return await this.client.post<any>('/users/commercial/private-customers', customerData);
  }

  /**
   * Obtener clientes privados (para comerciales)
   */
  public async getPrivateCustomers(): Promise<ApiResponse<any[]>> {
    return await this.client.get<any[]>('/users/commercial/private-customers');
  }

  /**
   * Actualizar un cliente privado (para comerciales)
   * @param customerId ID del cliente privado
   * @param customerData Datos actualizados
   */
  public async updatePrivateCustomer(
    customerId: string,
    customerData: Partial<{
      name: string;
      email: string;
      phone: string;
      address: {
        street: string;
        city: string;
        postalCode: string;
        country: string;
      };
      nif: string;
      notes: string;
    }>
  ): Promise<ApiResponse<any>> {
    return await this.client.put<any>(`/users/commercial/private-customers/${customerId}`, customerData);
  }

  /**
   * Eliminar un cliente privado (para comerciales)
   * @param customerId ID del cliente privado
   */
  public async deletePrivateCustomer(customerId: string): Promise<ApiResponse<null>> {
    return await this.client.delete<null>(`/users/commercial/private-customers/${customerId}`);
  }

  /**
   * Obtener estadísticas de usuarios (admin/beeadmin)
   */
  public async getUserStats(): Promise<ApiResponse<any>> {
    return await this.client.get<any>('/users/stats');
  }

  /**
   * Obtener las direcciones del usuario actual
   */
  public async getAddresses(): Promise<ApiResponse<UserAddress[]>> {
    return await this.client.get<UserAddress[]>('/auth/myaddresses');
  }

  /**
   * Añadir una nueva dirección al usuario actual
   * @param addressData Datos de la dirección
   */
  public async addAddress(addressData: Omit<UserAddress, 'id'>): Promise<ApiResponse<UserAddress>> {
    return await this.client.post<UserAddress>('/auth/myaddresses', addressData);
  }

  /**
   * Actualizar una dirección existente
   * @param addressId ID de la dirección
   * @param addressData Datos actualizados de la dirección
   */
  public async updateAddress(addressId: string, addressData: Omit<UserAddress, 'id'>): Promise<ApiResponse<UserAddress>> {
    return await this.client.put<UserAddress>(`/auth/myaddresses/${addressId}`, addressData);
  }

  /**
   * Eliminar una dirección
   * @param addressId ID de la dirección
   */
  public async deleteAddress(addressId: string): Promise<ApiResponse<null>> {
    return await this.client.delete<null>(`/auth/myaddresses/${addressId}`);
  }

  /**
   * Establecer una dirección como predeterminada
   * @param addressId ID de la dirección
   * @param type Tipo de dirección (shipping/billing)
   */
  public async setDefaultAddress(addressId: string, type: 'shipping' | 'billing'): Promise<ApiResponse<null>> {
    return await this.client.put<null>(`/auth/myaddresses/${addressId}/default`, { type });
  }

  /**
   * Obtener usuarios pendientes de aprobación (admin/beeadmin)
   */
  public async getPendingUsers(): Promise<ApiResponse<User[]>> {
    return await this.client.get<User[]>('/users/pending');
  }

  /**
   * Aprobar un usuario pendiente (admin/beeadmin)
   * @param id ID del usuario a aprobar
   */
  public async approveUser(id: string): Promise<ApiResponse<User>> {
    return await this.client.put<User>(`/users/${id}/approve`, {});
  }

  /**
   * Rechazar un usuario pendiente (admin/beeadmin)
   * @param id ID del usuario a rechazar
   * @param reason Motivo del rechazo (opcional)
   * @param deleteUser Si true, elimina el usuario de la BD
   */
  public async rejectUser(id: string, reason?: string, deleteUser: boolean = false): Promise<ApiResponse<any>> {
    return await this.client.put<any>(`/users/${id}/reject`, { reason, deleteUser });
  }

  /**
   * Asignar comerciales a un usuario con porcentaje de comisión (admin)
   * @param id ID del usuario
   * @param commercialIds IDs de los comerciales
   * @param commissionPercentage Porcentaje de comisión (0-100)
   */
  public async assignCommercials(id: string, commercialIds: string[], commissionPercentage?: number): Promise<ApiResponse<User>> {
    const response = await this.client.put<User>(`/users/${id}/assign-commercial`, { commercialIds, commissionPercentage });
    if (response.success) {
      this.client.invalidateCacheForEndpoint(`/users/${id}`);
    }
    return response;
  }

  /**
   * Compatibilidad con llamadas que todavía asignan un único comercial.
   */
  public async assignCommercial(id: string, commercialId: string | null, commissionPercentage?: number): Promise<ApiResponse<User>> {
    const response = await this.client.put<User>(`/users/${id}/assign-commercial`, { commercialId, commissionPercentage });
    if (response.success) {
      this.client.invalidateCacheForEndpoint(`/users/${id}`);
    }
    return response;
  }

  /**
   * Obtener clientes asignados al comercial actual
   */
  public async getMyClients(): Promise<ApiResponse<User[]>> {
    return await this.client.get<User[]>('/users/commercial/customers');
  }

  /**
   * Obtener clientes privados del comercial actual
   */
  public async getMyPrivateClients(): Promise<ApiResponse<any[]>> {
    return await this.client.get<any[]>('/users/commercial/private-customers');
  }

  /**
   * Actualizar la tarifa de un cliente registrado del comercial
   */
  public async updateCustomerTarifa(customerId: string, tarifa: number): Promise<ApiResponse<User>> {
    return await this.client.put<User>(`/users/commercial/customers/${customerId}/tarifa`, { tarifa });
  }
}
